import React, { useEffect, useRef, useState } from 'react';
import type { Payload } from './booking_sheet_workspace/utils';
import { previewFieldsByLabel } from './booking_sheet_workspace/utils';

export default function BookingSheetPreview({ url, payload, operationId, csrfToken, debounceMs = 500, onOverflowFieldsChange }: {
  url: string; payload: Payload; operationId: string; csrfToken: string; debounceMs?: number; onOverflowFieldsChange?: (fields: string[]) => void;
}) {
  const frame = useRef<HTMLIFrameElement>(null);
  const revision = useRef(0);
  const [error, setError] = useState('');
  const [updating, setUpdating] = useState(true);
  const [attempt, setAttempt] = useState(0);
  const [height, setHeight] = useState<number>();
  useEffect(() => {
    const requestId = ++revision.current;
    setError('');
    setUpdating(true);
    onOverflowFieldsChange?.([]);
    const controller = new AbortController();
    let bytes: ArrayBuffer | undefined;
    let ready = false;
    let disposed = false;
    let timeout: number | undefined;
    const fail = (message: string) => { window.clearTimeout(timeout); setUpdating(false); setError(message); onOverflowFieldsChange?.([]); };
    const send = () => {
      if (ready && bytes && frame.current?.contentWindow) {
        frame.current.contentWindow.postMessage({ type: 'booking-preview-document', requestId, data: bytes }, window.location.origin, [bytes]);
        bytes = undefined;
      }
    };
    const receive = (event: MessageEvent) => {
      if (event.origin !== window.location.origin || event.source !== frame.current?.contentWindow) return;
      if (event.data?.type === 'booking-preview-ready') { ready = true; send(); }
      if (event.data?.requestId !== requestId) return;
      if (event.data?.type === 'booking-preview-resize' && typeof event.data.height === 'number' && Number.isFinite(event.data.height) && event.data.height > 0) setHeight(event.data.height);
      if (event.data?.type === 'booking-preview-loaded') {
        window.clearTimeout(timeout); setUpdating(false);
        const fields = Array.isArray(event.data.overflowLabels) ? event.data.overflowLabels
          .filter((label: unknown): label is string => typeof label === 'string' && Object.hasOwn(previewFieldsByLabel, label))
          .map((label: string) => previewFieldsByLabel[label]) : [];
        onOverflowFieldsChange?.([...new Set<string>(fields)]);
      }
      if (event.data?.type === 'booking-preview-error') fail('The preview could not be displayed. Your draft is still available.');
    };
    window.addEventListener('message', receive);
    frame.current?.contentWindow?.postMessage({ type: 'booking-preview-connect' }, window.location.origin);
    const prepare = () => {
      timeout = window.setTimeout(() => { fail('The preview is taking too long. Please retry.'); controller.abort(); }, 30_000);
      fetch(url, {
      method: 'POST', credentials: 'same-origin', signal: controller.signal,
      headers: { 'Content-Type': 'application/json', Accept: 'application/pdf', 'X-CSRF-Token': csrfToken },
      body: JSON.stringify({ patient_operation_id: operationId, booking_sheet: { payload } }),
    }).then(async response => {
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        throw new Error(data?.errors?.join(' ') || 'Unable to prepare the preview. Please try again.');
      }
      if (!response.headers.get('content-type')?.includes('application/pdf')) throw new Error('Your session may have expired. Save your edits before reloading.');
      const result = await response.arrayBuffer();
      if (!disposed) { bytes = result; send(); }
    }).catch(reason => {
      if (!disposed && !controller.signal.aborted) fail(reason instanceof Error ? reason.message : 'Unable to prepare the preview.');
    });
    };
    const debounce = debounceMs > 0 ? window.setTimeout(prepare, debounceMs) : undefined;
    if (!debounceMs) prepare();
    return () => { disposed = true; controller.abort(); bytes = undefined; window.clearTimeout(debounce); window.clearTimeout(timeout); window.removeEventListener('message', receive); };
  }, [url, payload, operationId, csrfToken, attempt, debounceMs, onOverflowFieldsChange]);
  return <section aria-label="Hospital form preview" className="space-y-3">
    <h2 className="font-semibold">Hospital form preview</h2>
    <p role="status" className="sr-only">{error ? 'Preview unavailable' : updating ? 'Updating preview…' : 'Preview up to date'}</p>
    {error && <div role="alert" className="rounded-lg bg-amber-50 p-3 text-sm text-amber-900"><p>{error}</p><button type="button" className="mt-2 underline" onClick={() => setAttempt(value => value + 1)}>Retry preview</button></div>}
    <iframe key={attempt} ref={frame} src={url} title="Booking sheet preview" hidden={!!error} aria-busy={updating} style={{ height: height === undefined ? '75vh' : `${height}px` }} className={`min-h-[480px] w-full rounded-lg border border-neutral-200 bg-neutral-100 ${updating ? 'opacity-50' : ''}`} />
  </section>;
}
